import React, { useState, useEffect } from 'react';
import { Sparkles, Check, X as XIcon, Edit2 } from 'lucide-react';
import { useResumoProposal } from '../hooks/useResumoProposal';
import { formatDate } from '../utils/dateHelpers';

/**
 * ADR-015 Fase 3: banner de revisao de proposta de resumo gerada pelo Agent
 * para ofertas com resumo_source IN ('human','merged'). Aparece apenas
 * quando ha proposta pendente. 3 acoes: Aceitar / Editar+Aceitar / Rejeitar.
 */
const ResumoReviewBlock = ({ ofertaId, currentResumo, userEmail, onResumoUpdated }) => {
  const { proposal, loading, acceptProposal, rejectProposal } = useResumoProposal(ofertaId);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (proposal && editing) {
      setEditText(proposal.texto || '');
    }
  }, [proposal, editing]);

  if (loading || !proposal) return null;

  const proposedText = proposal.texto || '';
  const proposalDate = formatDate(proposal.created_at);

  const handleAccept = async () => {
    setBusy(true);
    const { error } = await acceptProposal({
      proposalId: proposal.id,
      acceptedText: proposedText,
      acceptedBy: userEmail,
      edited: false,
    });
    setBusy(false);
    if (error) {
      window.alert('Erro ao aceitar: ' + (error.message || 'desconhecido'));
      return;
    }
    onResumoUpdated?.();
  };

  const handleEditAndAccept = async () => {
    if (!editText.trim()) return;
    setBusy(true);
    const { error } = await acceptProposal({
      proposalId: proposal.id,
      acceptedText: editText,
      acceptedBy: userEmail,
      edited: true,
    });
    setBusy(false);
    if (error) {
      window.alert('Erro ao salvar: ' + (error.message || 'desconhecido'));
      return;
    }
    setEditing(false);
    onResumoUpdated?.();
  };

  const handleReject = async () => {
    if (!window.confirm('Rejeitar esta proposta? O resumo atual permanece inalterado.')) return;
    setBusy(true);
    const { error } = await rejectProposal({
      proposalId: proposal.id,
      rejectedBy: userEmail,
    });
    setBusy(false);
    if (error) window.alert('Erro: ' + (error.message || 'desconhecido'));
  };

  return (
    <div className="mb-4 rounded-lg border border-violet-300 bg-violet-50/30">
      <div className="p-3 border-b border-violet-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-violet-600" />
          <span className="text-sm font-semibold text-violet-900">Nova sugestão de resumo</span>
          <span className="text-[10px] text-slate-500">por {proposal.autor_id || 'agent'} · {proposalDate}</span>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* Versao atual (humana) */}
        <div className="rounded border border-orange-200 bg-orange-50/40 p-3">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-orange-700 mb-1 flex items-center gap-1">
            📝 Versão atual <span className="normal-case text-slate-500">(sua)</span>
          </div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
            {currentResumo || <span className="italic text-slate-400">— sem resumo registrado —</span>}
          </div>
        </div>

        {/* Proposta do Agent */}
        <div className="rounded border border-violet-300 bg-white p-3">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700 mb-1 flex items-center gap-1">
            🤖 Proposta do Agent
          </div>
          {editing ? (
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={Math.min(15, Math.max(5, (editText.match(/\n/g)?.length || 0) + 4))}
              className="w-full text-sm border border-slate-300 rounded p-2 focus:ring-2 focus:ring-orange-500 outline-none whitespace-pre-wrap"
              autoFocus
            />
          ) : (
            <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{proposedText}</div>
          )}
        </div>

        {/* Acoes */}
        <div className="flex items-center gap-2 flex-wrap pt-1">
          {!editing ? (
            <>
              <button
                onClick={handleAccept}
                disabled={busy || !userEmail}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:opacity-50"
                title={!userEmail ? 'Faça login' : 'Aceitar proposta'}
              >
                <Check size={14} /> Aceitar
              </button>
              <button
                onClick={() => { setEditText(proposedText); setEditing(true); }}
                disabled={busy || !userEmail}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white text-sm rounded hover:bg-orange-600 disabled:opacity-50"
              >
                <Edit2 size={13} /> Editar + Aceitar
              </button>
              <button
                onClick={handleReject}
                disabled={busy || !userEmail}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-200 text-slate-700 text-sm rounded hover:bg-slate-300 disabled:opacity-50"
              >
                <XIcon size={13} /> Rejeitar
              </button>
              {!userEmail && (
                <span className="text-[11px] text-slate-500 italic">Faça login para revisar</span>
              )}
            </>
          ) : (
            <>
              <button
                onClick={handleEditAndAccept}
                disabled={busy || !editText.trim()}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50"
              >
                <Check size={14} /> Salvar versão editada
              </button>
              <button
                onClick={() => { setEditing(false); setEditText(''); }}
                disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-200 text-slate-700 text-sm rounded hover:bg-slate-300 disabled:opacity-50"
              >
                Cancelar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumoReviewBlock;
